import { createQuery, defineQuery, QueryableResults } from "@nova/queryable"
import { Table } from "@nova/ds-component-table"
import { Status } from "@nova/ds-component-status"
import { ButtonTertiary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import {
    useConfirmationModalContext,
    getGenericConfirmationModalComponent,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useDeleteMaintenanceRecord } from "features/pilot-tracking/maintenance/MaintenanceRecordQueries"
import { Fragment, useMemo, useState } from "react"
import { EditMaintenanceRecordModal } from "features/pilot-tracking/aircraft/EditMaintenanceRecordModal"
import { useParams } from "react-router"
import {
    AircraftMaintenanceServicesSection,
    ServiceLink,
} from "features/pilot-tracking/aircraft/AircraftMaintenanceServicesSection"
import { servicesQuery } from "features/pilot-tracking/aircraft/AircraftStatusQueries"
import { resolveDroneModelKey } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"

const globalServicesQuery = createQuery({
    resource: "maintenance_templates",
    select: [
        "id",
        "name",
        "description",
        "instructions",
        "condition_usage_flights",
        "condition_duration_hours",
        "condition_interval_value",
        "status_on_trigger",
    ],
    filters: [{ field: "apply_to_all", op: "=", value: true }],
})

const modelServicesQuery = createQuery({
    resource: "maintenance_templates",
    select: [
        "id",
        "name",
        "description",
        "instructions",
        "condition_usage_flights",
        "condition_duration_hours",
        "condition_interval_value",
        "status_on_trigger",
        "model_targets",
    ],
    filters: [{ field: "apply_to_all", op: "=", value: false }],
})

const aircraftModelQuery = (aircraftId: string) =>
    createQuery({
        resource: "aircraft",
        select: ["id", "manufacturer", "model"],
        filters: [{ field: "id", op: "=", value: aircraftId }],
    })

type GlobalServiceRow = QueryableResults<typeof globalServicesQuery>[number]
type ModelServiceRow = QueryableResults<typeof modelServicesQuery>[number]

const templateRowToServiceLink = (row: GlobalServiceRow | ModelServiceRow): ServiceLink =>
    ({
        template_id: row.id,
        "template.name": row.name,
        "template.description": row.description,
        "template.instructions": row.instructions,
        "template.condition_usage_flights": row.condition_usage_flights,
        "template.condition_duration_hours": row.condition_duration_hours,
        "template.condition_interval_value": row.condition_interval_value,
        "template.status_on_trigger": row.status_on_trigger,
    }) as ServiceLink

export const AircraftPageMaintenanceTab = () => {
    const { aircraftId = "" } = useParams()

    const queries = useMemo(
        () => ({
            services: servicesQuery(aircraftId),
            globalServices: globalServicesQuery,
            modelServices: modelServicesQuery,
            aircraft: aircraftModelQuery(aircraftId),
            records: maintenanceRecordsQuery(aircraftId),
        }),
        [aircraftId]
    )
    const { data } = useBatchPilotTrackingQuery(queries)

    const allServices = useMemo((): ServiceLink[] => {
        const specific = data?.services ?? []
        const includedIds = new Set(specific.map((s: ServiceLink) => s.template_id as string))
        const global = (data?.globalServices ?? ([] as GlobalServiceRow[]))
            .filter((row) => !includedIds.has(row.id as string))
            .map((row) => {
                includedIds.add(row.id as string)
                return templateRowToServiceLink(row)
            })
        const drone = data?.aircraft?.[0]
        const modelKey = drone
            ? (resolveDroneModelKey(drone.manufacturer, drone.model) ?? null)
            : null
        const model = (data?.modelServices ?? ([] as ModelServiceRow[]))
            .filter(
                (row) =>
                    !includedIds.has(row.id as string) &&
                    modelKey != null &&
                    Array.isArray(row.model_targets) &&
                    (row.model_targets as string[]).includes(modelKey)
            )
            .map(templateRowToServiceLink)
        return [...specific, ...global, ...model]
    }, [data?.services, data?.globalServices, data?.modelServices, data?.aircraft])

    const cardQueries = useMemo(
        () =>
            Object.fromEntries(
                allServices.map((link: ServiceLink) => [
                    link.template_id as string,
                    lastMaintenanceRecordQuery(aircraftId, link.template_id as string),
                ])
            ),
        [allServices, aircraftId]
    )
    const { data: cardData } = useBatchPilotTrackingQuery(cardQueries)

    return (
        <div className="pb-6 flex flex-col gap-8 h-full">
            <AircraftMaintenanceServicesSection
                aircraftId={aircraftId}
                data={allServices}
                cardRecordData={cardData as Record<string, { last_performed: string | null }[]>}
            />
            <div className="flex flex-col gap-4 h-full min-h-[250px]">
                <h3 className="text-white">History</h3>
                <AircraftMaintenanceHistoryTable data={data?.records ?? []} />
            </div>
        </div>
    )
}

const lastMaintenanceRecordQuery = defineQuery((aircraftId: string, templateId: string) => ({
    resource: "maintenance_records",
    aggregates: [{ field: "date_performed", func: "MAX", alias: "last_performed" }],
    filters: [
        { field: "aircraft_id", op: "=", value: aircraftId },
        { field: "template_id", op: "=", value: templateId },
    ],
}))

const maintenanceRecordsQuery = defineQuery((aircraftId: string) => ({
    resource: "maintenance_records",
    select: [
        "id",
        "date_performed",
        "performed_by",
        "notes",
        "skipped",
        "template.name",
        "template.description",
        "template.instructions",
        "template.condition_usage_flights",
        "template.condition_duration_hours",
        "template.condition_interval_value",
        "flights_since_last_service",
        "hours_since_last_service",
        "days_since_last_service",
    ],
    filters: [{ field: "aircraft_id", op: "=", value: aircraftId }],
    orderBy: [{ field: "date_performed", dir: "DESC" }],
}))

type MaintenanceRecord = QueryableResults<typeof maintenanceRecordsQuery>[number]

const DeleteRecordConfirmModal = getGenericConfirmationModalComponent({
    title: "Delete Record?",
    message: "Are you sure you want to delete this record?",
    confirmText: "Discard Changes",
})

const AircraftMaintenanceHistoryTable = ({
    data,
    onDelete,
    onEdit,
}: {
    data: MaintenanceRecord[]
    onDelete?: () => void
    onEdit?: () => void
}) => {
    const users = useAppSelector(selectAllRegisteredMembers)
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
    const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(null)
    const modal = useConfirmationModalContext()
    const { deleteMaintenanceRecord } = useDeleteMaintenanceRecord()

    const handleDelete = (recordId: string) => {
        modal
            .confirm({
                RenderComponent: DeleteRecordConfirmModal,
                asyncConfirmCallback: async () => void (await deleteMaintenanceRecord(recordId)),
            })
            .then(() => onDelete?.())
    }

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }

    if (!data) return null

    return (
        <>
            {editingRecord && (
                <EditMaintenanceRecordModal
                    record={editingRecord}
                    onClose={() => setEditingRecord(null)}
                    onSuccess={() => {
                        setEditingRecord(null)
                        onEdit?.()
                    }}
                />
            )}
            <div className="relative h-full">
                {data.length === 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none">
                        <h2 className="text-white">No maintenance records yet</h2>
                        <p className="text-gray-400 text-sm text-center max-w-xs">
                            Log your first maintenance record to start building history.
                        </p>
                    </div>
                )}
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header className="w-10" />
                            <Table.Header>
                                <div>Type</div>
                            </Table.Header>
                            <Table.Header>
                                <div>Status</div>
                            </Table.Header>
                            <Table.Header>
                                <div>Date performed</div>
                            </Table.Header>
                            <Table.Header>
                                <div>Performed by</div>
                            </Table.Header>
                            <Table.Header className="w-48" />
                        </Table.Row>
                    </Table.Head>
                    <Table.Body className="h-full">
                        {data.map((record) => {
                            const isExpanded = expandedIds.has(record.id)
                            const performedBy = users?.find((u) => u.id === record.performed_by)
                            const dateStr = record.date_performed
                                ? new Date(record.date_performed).toLocaleDateString("en-US", {
                                      month: "long",
                                      day: "numeric",
                                      year: "numeric",
                                  })
                                : "—"

                            return (
                                <Fragment key={record.id}>
                                    <Table.Row
                                        className="group cursor-pointer"
                                        onClick={() => toggleExpand(record.id)}
                                    >
                                        <Table.Cell>
                                            <div className="text-gray-300 flex items-center">
                                                <Icons.Icon
                                                    Icon={
                                                        isExpanded
                                                            ? Icons.ArrowDropdown
                                                            : Icons.ArrowDropdownRight
                                                    }
                                                />
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <h4 className="text-gray-0">
                                                {record["template.name"] ?? "—"}
                                            </h4>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {record.skipped ? (
                                                <Status type="warning" variant="outline">
                                                    Skipped
                                                </Status>
                                            ) : (
                                                <Status type="positive" variant="outline">
                                                    Completed
                                                </Status>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <h5 className="text-gray-100">{dateStr}</h5>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <h5 className="text-gray-100">
                                                {performedBy?.fullName ?? "—"}
                                            </h5>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 pr-2">
                                                <ButtonTertiary
                                                    title="Edit Record"
                                                    LeftIcon={Icons.Edit}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setEditingRecord(record)
                                                    }}
                                                />
                                                <IconButton
                                                    className="text-red-300 hover:!text-red-500"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleDelete(record.id)
                                                    }}
                                                >
                                                    <Icons.SmallIcon Icon={Icons.Trash} />
                                                </IconButton>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                    {isExpanded && (
                                        <Table.Row key={`${record.id}-expanded`}>
                                            <Table.Cell colSpan={6}>
                                                <div className="flex gap-16 px-4 py-4">
                                                    <div className="flex flex-col gap-3">
                                                        <small className="text-gray-300 tracking-wide">
                                                            USAGE SINCE LAST SERVICE
                                                        </small>
                                                        <UsageRow
                                                            label="Flights"
                                                            value={
                                                                record.flights_since_last_service
                                                            }
                                                        />
                                                        <UsageRow
                                                            label="Hours"
                                                            value={
                                                                record.hours_since_last_service !=
                                                                null
                                                                    ? Math.round(
                                                                          record.hours_since_last_service *
                                                                              10
                                                                      ) / 10
                                                                    : null
                                                            }
                                                        />
                                                        <UsageRow
                                                            label="Days"
                                                            value={record.days_since_last_service}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col gap-3 flex-1">
                                                        <small className="text-gray-300 tracking-wide">
                                                            NOTES
                                                        </small>
                                                        <p className="text-gray-100 text-sm">
                                                            {record.notes ?? "—"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Fragment>
                            )
                        })}
                    </Table.Body>
                </Table>
            </div>
        </>
    )
}

const UsageRow = ({ label, value }: { label: string; value: number | null | undefined }) => (
    <div className="flex items-center gap-8">
        <span className="text-gray-300 text-sm w-12">{label}</span>
        <span className="text-white text-sm font-medium">{value ?? "—"}</span>
    </div>
)

export { AircraftMaintenanceHistoryTable }
